import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { totalTypeDropdownSelectorOptions } from "components/dashboard/dataset-table/dataset-table-editor/defaultDatasetTableEditorConfigs";
import { DatasetDefaultConfig, DatasetFullDefaultConfig } from "models/dataset-models";
import { Button } from "primereact/button";
import { ColorPicker } from "primereact/colorpicker";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent, DataTableRowReorderEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { lazy, Suspense, useState } from "react";
import { T } from "services/lang/LanguageService";

const HighlightsEditor = lazy(
  () => import("components/dashboard/dataset-table/dataset-table-editor/highlights-editor/HighlightsEditor")
);

interface DatasetTableDatasetEditorProps {
  currentConfig: DatasetFullDefaultConfig;
  setCurrentConfig: React.Dispatch<React.SetStateAction<DatasetFullDefaultConfig>>;
  onConfigChange: React.Dispatch<React.SetStateAction<DatasetFullDefaultConfig>>;
}

enum DefaultHighlightColor {
  defaultTextColor = "#000000",
  defaultBackgroundColor = "#FFFFFF",
}

const DatasetTableDatasetEditor = ({
  currentConfig,
  setCurrentConfig,
  onConfigChange,
}: DatasetTableDatasetEditorProps) => {
  const [highlightDialogIdentifier, setHighlightDialogIdentifier] = useState("");
  const [highlightDialogVisible, setHighlightDialogVisible] = useState(false);
  const [showTotal, setShowTotal] = useState(false);

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={e => options.editorCallback?.(e.target.value)}
        style={{ width: "100%" }}
      />
    );
  };

  const numberEditor = (options: ColumnEditorOptions) => {
    return (
      <InputNumber
        value={options.value}
        onChange={e => options.editorCallback?.(e.value)}
        inputStyle={{ width: "100%" }}
      />
    );
  };

  const switchEditor = (options: ColumnEditorOptions) => {
    return (
      <InputSwitch
        checked={options.value}
        onChange={e => {
          options.editorCallback?.(e.value);
          setShowTotal(e.value);
        }}
      />
    );
  };

  const totalTypeEditor = (options: ColumnEditorOptions) => {
    if (showTotal) {
      return (
        <Dropdown
          value={options.value}
          options={totalTypeDropdownSelectorOptions}
          onChange={e => options.editorCallback?.(e.value)}
          style={{ width: "100%" }}
        />
      );
    }
    return <Dropdown disabled style={{ width: "100%" }} />;
  };

  const defaultHighlightEditor = (options: ColumnEditorOptions) => {
    return (
      <div className="flex justify-content-center align-items-center">
        <ColorPicker
          value={options.rowData.defaultHighlight?.color ?? DefaultHighlightColor.defaultTextColor}
          onChange={e =>
            options.editorCallback?.({
              color: `#${e.value}`,
              bgColor: options.rowData.defaultHighlight?.bgColor,
            })
          }
        />
        <ColorPicker
          value={options.rowData.defaultHighlight?.bgColor ?? DefaultHighlightColor.defaultBackgroundColor}
          onChange={e =>
            options.editorCallback?.({
              color: options.rowData.defaultHighlight?.color,
              bgColor: `#${e.value}`,
            })
          }
        />
        <i
          className="pi pi-refresh ml-2 cursor-pointer"
          onClick={() =>
            options.editorCallback?.({
              color: DefaultHighlightColor.defaultTextColor,
              bgColor: DefaultHighlightColor.defaultBackgroundColor,
            })
          }
        />
      </div>
    );
  };

  const highlightBodyTemplate = (rowData: DatasetDefaultConfig) => {
    return (
      <Button
        label={T("action.show")}
        className="p-button-sm p-button-text p-button-outlined"
        onClick={() => handleShowHighlightsClicked(rowData.identifier)}
      />
    );
  };

  const defaultHighlightBodyTemplate = (rowData: DatasetDefaultConfig) => {
    let tempColor = rowData.defaultHighlight ? rowData.defaultHighlight?.color : "#000000";
    let tempBgColor = rowData.defaultHighlight ? rowData.defaultHighlight?.bgColor : "#FFFFFF";
    tempColor = tempColor?.startsWith("#") ? tempColor : `#${tempColor}`;
    tempBgColor = tempBgColor?.startsWith("#") ? tempBgColor : `#${tempBgColor}`;
    return (
      <span className="p-1 pl-2 pr-2" style={{ color: tempColor, backgroundColor: tempBgColor }}>
        {T("word.text")}
      </span>
    );
  };

  const handleShowHighlightsClicked = (identifier: string) => {
    setHighlightDialogVisible(true);
    setHighlightDialogIdentifier(identifier);
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    if (currentConfig.datasets) {
      const datasetsToUpdate = [...currentConfig.datasets!];
      const { newData, index } = e;

      if (newData.defaultHighlight) {
        if (newData.defaultHighlight.bgColor === undefined) {
          newData.defaultHighlight.bgColor = DefaultHighlightColor.defaultBackgroundColor;
        }
        if (newData.defaultHighlight.color === undefined) {
          newData.defaultHighlight.color = DefaultHighlightColor.defaultTextColor;
        }
      }

      datasetsToUpdate[index] = newData as DatasetDefaultConfig;
      setCurrentConfig({ ...currentConfig, datasets: datasetsToUpdate });
      onConfigChange({ ...currentConfig, datasets: datasetsToUpdate });
    } else {
      const { newData } = e;

      if (newData.defaultHighlight) {
        if (newData.defaultHighlight.bgColor === undefined) {
          newData.defaultHighlight.bgColor = DefaultHighlightColor.defaultBackgroundColor;
        }
        if (newData.defaultHighlight.color === undefined) {
          newData.defaultHighlight.color = DefaultHighlightColor.defaultTextColor;
        }
      }

      setCurrentConfig(newData);
    }
  };

  const identifierEditor = (options: ColumnEditorOptions) => {
    return <DatasetDropdown value={options.value || ""} onChange={e => options.editorCallback?.(e)} />;
  };

  const titleBodyTemplate = (rowData: DatasetDefaultConfig) => {
    return <DatasetTitle identifier={rowData.identifier} />;
  };

  const handleRowReorder = (e: DataTableRowReorderEvent<DatasetDefaultConfig[]>) => {
    setCurrentConfig({ ...currentConfig, datasets: e.value });
    onConfigChange({ ...currentConfig, datasets: e.value });
  };

  return currentConfig.datasets ? (
    <>
      <DataTable
        value={currentConfig.datasets}
        editMode="row"
        onRowEditComplete={onRowEditComplete}
        className="w-full"
        reorderableRows
        onRowReorder={e => handleRowReorder(e)}
      >
        <Column rowReorder style={{ width: "5%" }} />
        <Column
          className="p-0"
          field="identifier"
          header={T("word.title")}
          style={{ width: "25%" }}
          align="center"
          editor={options => identifierEditor(options)}
          body={titleBodyTemplate}
        />
        <Column
          className="p-0"
          field="decimalPlaces"
          header={T("word.decimal")}
          style={{ width: "5%" }}
          align="center"
          editor={options => numberEditor(options)}
        />
        <Column
          className="p-0"
          field="overriddenTitle"
          header={T("word.overridden-title")}
          style={{ width: "15%" }}
          align="center"
          editor={options => textEditor(options)}
        />
        <Column
          className="p-0"
          field="description"
          header={T("word.description")}
          style={{ width: "25%" }}
          align="center"
          editor={options => textEditor(options)}
        />
        <Column
          className="p-0"
          field="showTotal"
          header={T("component.show-total")}
          style={{ width: "10%" }}
          align="center"
          editor={options => switchEditor(options)}
        />
        <Column
          className="p-0"
          field="totalType"
          header={T("component.total-type")}
          style={{ width: "10%" }}
          align="center"
          editor={options => totalTypeEditor(options)}
        />
        <Column
          className="p-0"
          field="defaultHighlight"
          style={{ width: "10%" }}
          header={T("word.default-highlight")}
          headerStyle={{ width: "10%" }}
          align="center"
          editor={options => defaultHighlightEditor(options)}
          body={defaultHighlightBodyTemplate}
        />
        <Column
          className="p-0"
          style={{ width: "10%" }}
          header={T("word.highlight-ranges")}
          headerStyle={{ width: "10%" }}
          align="center"
          body={highlightBodyTemplate}
        />
        <Column
          className="p-0"
          rowEditor
          style={{ width: "10%" }}
          headerStyle={{ width: "10%" }}
          bodyStyle={{ textAlign: "center" }}
        />
      </DataTable>
      <Dialog
        onHide={() => setHighlightDialogVisible(false)}
        visible={highlightDialogVisible}
        header={T("action.edit-highlights")}
        style={{ width: "min(100vw, 800px)", height: "500x" }}
      >
        <Suspense fallback={<ProgressSpinner />}>
          <HighlightsEditor
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            onConfigChange={onConfigChange}
            identifier={highlightDialogIdentifier}
          />
        </Suspense>
      </Dialog>
    </>
  ) : (
    <>
      <DataTable
        value={[currentConfig]}
        editMode="row"
        onRowEditComplete={onRowEditComplete}
        className="w-full"
      >
        <Column
          className="p-0"
          field="identifier"
          header={T("word.title")}
          style={{ width: "30%" }}
          align="center"
          body={titleBodyTemplate}
        />
        <Column
          className="p-0"
          field="decimalPlaces"
          header={T("word.decimal")}
          style={{ width: "5%" }}
          align="center"
          editor={options => numberEditor(options)}
        />
        <Column
          className="p-0"
          field="overriddenTitle"
          header={T("word.overridden-title")}
          style={{ width: "15%" }}
          align="center"
          editor={options => textEditor(options)}
        />
        <Column
          className="p-0"
          field="description"
          header={T("word.description")}
          style={{ width: "25%" }}
          align="center"
          editor={options => textEditor(options)}
        />
        <Column
          className="p-0"
          field="showTotal"
          header={T("component.show-total")}
          style={{ width: "10%" }}
          align="center"
          editor={options => switchEditor(options)}
        />
        <Column
          className="p-0"
          field="totalType"
          header={T("component.total-type")}
          style={{ width: "10%" }}
          align="center"
          editor={options => totalTypeEditor(options)}
        />
        <Column
          className="p-0"
          field="defaultHighlight"
          style={{ width: "10%" }}
          header={T("word.default-highlight")}
          headerStyle={{ width: "10%" }}
          align="center"
          editor={options => defaultHighlightEditor(options)}
          body={defaultHighlightBodyTemplate}
        />
        <Column
          className="p-0"
          style={{ width: "10%" }}
          header={T("word.highlight-ranges")}
          headerStyle={{ width: "10%" }}
          align="center"
          body={highlightBodyTemplate}
        />
        <Column
          className="p-0"
          rowEditor
          style={{ width: "10%" }}
          headerStyle={{ width: "10%" }}
          bodyStyle={{ textAlign: "center" }}
        />
      </DataTable>
      <Dialog
        onHide={() => setHighlightDialogVisible(false)}
        visible={highlightDialogVisible}
        header={T("action.edit-highlights")}
        style={{ width: "min(100vw, 800px)", height: "500x" }}
      >
        <Suspense fallback={<ProgressSpinner />}>
          <HighlightsEditor
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            onConfigChange={onConfigChange}
            identifier={highlightDialogIdentifier}
          />
        </Suspense>
      </Dialog>
    </>
  );
};

export default DatasetTableDatasetEditor;
